function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;

  _setPrototypeOf(subClass, superClass);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

import _ from 'underscore';
import $ from 'jquery';
import Backbone from '@polleverywhere/backbone';
import { autolink } from '@polleverywhere/core/lib/util';
import BEM from '@polleverywhere/toolkit-lib/lib/mixins/_bem';
import NotificationTpl from './templates/notification.hamlc';
import DOMPurify from 'dompurify';
import IconNotice from '@polleverywhere/assets/icons/info-blue.svg';
import IconError from '@polleverywhere/assets/icons/error-red.svg';
import IconWarning from '@polleverywhere/assets/icons/warning-yellow.svg';
import IconSuccess from '@polleverywhere/assets/icons/ok-green.svg';
import './style.scss';

var setClass = function setClass(self, type) {
  var classNames = ['error', 'warning', 'notice', 'success'];
  classNames.forEach(function (className) {
    return self.$_toggleModifier(null, self.className, className, false);
  });
  self.$_toggleModifier(null, self.className, type, true);
};

var show = function show(self, message, options) {
  _.defaults(options, {
    autoHideAfter: self.options.autoHideAfter,
    autoLink: self.options.autoLink
  });

  var msg = DOMPurify.sanitize(message, {
    SAFE_FOR_JQUERY: true
  });

  if (options.autoLink) {
    msg = autolink(msg);
  }

  self.$message.html(msg);
  self.renderLabels();
  self.$_toggleModifier(null, self.className, 'hidden', false);
  hide(self, options.autoHideAfter);
};
/**
 * Hide after a delay
 * Uses configured autoHide duration by default
 */


var hide = function hide(self, delay) {
  if (delay < 0) {
    return;
  }

  clearTimeout(self.hideTimeout);
  self.hideTimeout = setTimeout(function () {
    self.$_toggleModifier(null, self.className, 'hidden', true);
    self.trigger('dismiss');
  }, delay);
};
/**
 * The notification component is used to display information and application
 * status messages in a consistent place. It should be used for creation status
 * updates and error messages.
 *
 * @event dismiss When the notification has been dismissed
 * @event click:primary When the primary action of the notification has been clicked
 * @event click:dismiss When the dismiss action of the notification has been clicked
 *
 * @example Sticky notification that shows immediately
 *   nag = new Component.Notification(autoHideAfter: -1)
 *   $(parentView).append nag.render().$el
 *
 *   nag.notice 'Your sticky message here'
 *
 * @example Hidden initially
 *   view = new Component.Notification(autoHideAfter: 1000)
 *   $(parentView).append view.render().$el
 *
 *   view.notice 'Your notification here'
 *
 * @public
 */


var Notification = /*#__PURE__*/function (_Backbone$View) {
  _inheritsLoose(Notification, _Backbone$View);

  function Notification() {
    return _Backbone$View.apply(this, arguments) || this;
  }

  var _proto = Notification.prototype;
  /**
   * Construct notification object.
   * @param {Object} [options]
   * @param {Number} [options.autoHideAfter=5000] Milliseconds before notification is hidden. -1 will allow the notification to show indefinitely unless dismiss is triggered.
   * @param {string} [options.mode=null]
   *   Default is like 'banner' without class modifier for legacy compat
   *   Set to 'banner' for full width
   *   Set to 'banner-alert' for full width, full color
   *   Set to 'toast' for narrow width
   * @param {string} [options.theme=light] Which color theme to use, options are: light, dark
   * @param {Function} [options.linkHandler=null] Use a custom handler for links in messages, prevents default handling
   */

  _proto.initialize = function initialize(options) {
    if (options === void 0) {
      options = {};
    }

    this.options = options;

    _.extend(this, BEM(this));

    _.defaults(this.options, this.defaults);

    this.$_toggleModifier(null, this.className, 'hidden', true);
    this.$_toggleModifier(null, this.className, this.options.theme, true);

    if (this.options.mode) {
      this.$_toggleModifier(null, this.className, this.options.mode, true);
    }
  };

  _proto.renderLabels = function renderLabels() {
    if (this.options.primaryLabel) {
      this.$('button[name=primary]').show();
      this.$('button[name=primary]').attr({
        title: this.options.primaryLabel
      });
    } else {
      this.$('button[name=primary]').hide();
    }
  } // Generate view DOM elements
  ;

  _proto.render = function render() {
    this.$el.html(this.template());
    this.$message = this.$_element('message');
    this.renderLabels();
    return this;
  }
  /**
   * Displays a notice in a blue box (white for toasts)
   * @param {String} message Message to display
   * @param {Object} [options]
   * @param {Number} [options.autoHideAfter] Override default autoHideAfter
   * @param {String|HTMLElement} [options.icon] Override default icon
   */
  ;

  _proto.notice = function notice(message, options) {
    if (options === void 0) {
      options = {};
    }

    setClass(this, 'notice');
    show(this, message, options);
    this.$_toggleModifier(null, this.className, 'custom-icon', !!options.icon);
    this.$_element('icon').empty().append(options.icon || IconNotice);
  }
  /**
   * Displays an error message in a red box (white for toasts)
   * @param {String} message Message to display
   * @param {Object} [options]
   * @param {Number} [options.autoHideAfter] Override default autoHideAfter
   */
  ;

  _proto.error = function error(message, options) {
    if (options === void 0) {
      options = {};
    }

    setClass(this, 'error');
    show(this, message, options);
    this.$_toggleModifier(null, this.className, 'custom-icon', false);
    this.$_element('icon').empty().append(IconError);
  }
  /**
   * Displays an warning message in a yellow box (white for banner alerts and toasts)
   * @param {String} message Message to display
   * @param {Object} [options]
   * @param {Number} [options.autoHideAfter] Override default autoHideAfter
   */
  ;

  _proto.warning = function warning(message, options) {
    if (options === void 0) {
      options = {};
    }

    setClass(this, 'warning');
    show(this, message, options);
    this.$_toggleModifier(null, this.className, 'custom-icon', false);
    this.$_element('icon').empty().append(IconWarning);
  }
  /**
   * Displays an success message in a green box (white for toasts)
   * @param {String} message Message to display
   * @param {Object} [options]
   * @param {Number} [options.autoHideAfter] Override default autoHideAfter
   */
  ;

  _proto.success = function success(message, options) {
    if (options === void 0) {
      options = {};
    }

    setClass(this, 'success');
    show(this, message, options);
    this.$_toggleModifier(null, this.className, 'custom-icon', false);
    this.$_element('icon').empty().append(IconSuccess);
  } // Hide the notification immediately
  ;

  _proto.dismiss = function dismiss() {
    hide(this, 0);
  } // Remove from the DOM and clear the timeout
  ;

  _proto.remove = function remove() {
    clearTimeout(this.hideTimeout);

    _Backbone$View.prototype.remove.call(this);
  };

  _createClass(Notification, [{
    key: "className",
    get: function get() {
      return 'component-notification';
    }
  }, {
    key: "template",
    get: function get() {
      return NotificationTpl;
    }
  }, {
    key: "defaults",
    get: function get() {
      return {
        autoHideAfter: 5000,
        mode: null,
        autoLink: false,
        theme: 'light',
        primaryLabel: false
      };
    }
  }, {
    key: "events",
    get: function get() {
      return {
        'click .component-notification__message a': function clickComponentNotification__messageA(e) {
          if (this.options.linkHandler) {
            e.preventDefault();
            var $link = $(e.target);
            this.options.linkHandler($link.prop('href'), $link);
          }
        },
        'click button[name=primary]': function clickButtonNamePrimary() {
          this.trigger('click:primary');
        },
        'click button[name=dismiss]': function clickButtonNameDismiss() {
          this.trigger('click:dismiss');
          this.dismiss();
        }
      };
    }
  }]);

  return Notification;
}(Backbone.View);

export { Notification as default };